import { useId, type ReactNode } from "react";
import { Button, Tooltip, type LucideIconName } from "@/deps/ui-library";
import type { Permission } from "@/api/types";
import { useCan } from "@/app/session";
import { PERMISSIONS } from "@/lib/permissions";

/**
 * An action button that disables itself, with a reason, when the person cannot do it.
 *
 * The RDS rule is "disable actions with a reason", and the reason matters more than the disabling:
 * a greyed-out button with no explanation is indistinguishable from a broken one, and people file
 * bugs about it.
 *
 * The reason is an RDS `Tooltip`, not a `title`. The refused button is `aria-disabled` rather than
 * `disabled`: a disabled button takes no focus and, in most browsers, no pointer events, so a
 * tooltip on it would never open and the reason would be mouse-only at best. This one stays in the
 * tab order, says it is unavailable, does nothing when pressed, and carries the reason as its
 * description (`aria-describedby`), so a screen reader reads it with the name.
 *
 * The route's `RequirePermission` is still the backstop — this is the courtesy, not the control.
 * The API is the actual enforcement point (ADR-0005).
 */
export function PermittedAction({
  permission,
  onClick,
  children,
  // A header action is secondary (ADR-0093): a filled primary is a form's final submit.
  variant = "secondary",
  leftIcon,
}: {
  permission: Permission;
  onClick: () => void;
  children: ReactNode;
  variant?: "solid" | "secondary" | "ghost";
  leftIcon?: LucideIconName;
}) {
  const can = useCan();
  const allowed = can(permission);
  const reasonId = useId();

  if (allowed) {
    return (
      <Button
        colorScheme="primary"
        variant={variant}
        size="sm"
        leftIcon={leftIcon}
        onClick={onClick}
      >
        {children}
      </Button>
    );
  }

  const reason = `${PERMISSIONS[permission]} is not part of your role.`;

  return (
    <>
      <Tooltip content={reason} placement="bottom">
        <Button
          colorScheme="primary"
          variant={variant}
          size="sm"
          leftIcon={leftIcon}
          aria-disabled="true"
          aria-describedby={reasonId}
          // Unavailable, not inert: it keeps focus so the reason can be reached, and does nothing.
          onClick={(event) => event.preventDefault()}
          className="aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
        >
          {children}
        </Button>
      </Tooltip>
      <span id={reasonId} className="sr-only">
        {reason}
      </span>
    </>
  );
}
